import { getFontEmbedCSS, toBlob } from 'html-to-image';
import { W, H, EXPORT_SCALE } from './tokens';

/**
 * The self-hosted fonts come out as 67kB of inlined CSS that is the same for
 * every card, so fetch and encode it once. A failure here is not worth losing
 * the card over: the raster falls back to working it out itself.
 */
let fontCss: Promise<string | undefined> | undefined;
function embeddedFonts(node: HTMLElement): Promise<string | undefined> {
  fontCss ??= getFontEmbedCSS(node).catch((err) => { console.warn('[kindling] font embed failed', err); return undefined; });
  return fontCss;
}

/**
 * How long a raster may take before it is called a failure.
 *
 * html-to-image resolves its images through `img.decode()` with no catch on the
 * rejection, so a picture the browser refuses to decode leaves a promise that
 * never settles — and the screen sits on "Pinning it up…" for ever, with the
 * card made and no button that works. A slow phone's first card takes about
 * seven seconds, so this is generous; what it rules out is only the hang.
 */
const RASTER_TIMEOUT_MS = 30_000;

/**
 * Safari draws the SVG that html-to-image builds before the pictures inside it
 * have decoded, so a card with a photograph comes out with the photograph's
 * frame and nothing in it. A second pass finds them decoded. A photographed
 * drawing on an iPhone reached the feed as an empty tilted frame this way
 * (2026-09-21), while Chromium had it on the first pass.
 *
 * So a card with a picture is drawn again until two passes agree on size (a
 * picture that arrives makes the PNG much larger), at most four times. Without
 * a picture there is nothing to wait for and one pass is the card.
 */
const MAX_PASSES = 4;
async function rasterSettled(node: HTMLElement, fontEmbedCSS: string | undefined, hasPicture: boolean): Promise<Blob | null> {
  const pass = () => toBlob(node, { width: W, height: H, pixelRatio: EXPORT_SCALE, cacheBust: false, skipFonts: false, fontEmbedCSS });
  let blob = await pass();
  if (!hasPicture) return blob;
  for (let i = 1; i < MAX_PASSES; i++) {
    const next = await pass();
    if (!next) break;
    const settled = blob !== null && Math.abs(next.size - blob.size) <= blob.size * 0.02;
    blob = next;
    if (settled) break;
  }
  return blob;
}

/**
 * Rasterises the card markup to a 1080×1350 PNG in the browser, the moment
 * the timer stops (10 §6). The markup is mounted off-screen so the fonts
 * (self-hosted in /fonts) and any photograph are laid out for real.
 */
export async function cardToPng(html: string): Promise<Blob> {
  const host = document.createElement('div');
  host.style.cssText = `position:fixed;left:-10000px;top:0;width:${W}px;height:${H}px;overflow:hidden;pointer-events:none;`;
  host.innerHTML = html;
  document.body.appendChild(host);
  try {
    await document.fonts.ready;
    await Promise.all(Array.from(host.querySelectorAll('img')).map((img) => img.complete ? Promise.resolve() : new Promise<void>((r) => { img.onload = () => r(); img.onerror = () => r(); })));
    const node = host.firstElementChild as HTMLElement;
    const fontEmbedCSS = await embeddedFonts(node);
    let bell: ReturnType<typeof setTimeout> | undefined;
    const blob = await Promise.race([
      rasterSettled(node, fontEmbedCSS, host.querySelector('img') !== null),
      new Promise<never>((_, reject) => { bell = setTimeout(() => reject(new Error('card render timed out')), RASTER_TIMEOUT_MS); }),
    ]).finally(() => clearTimeout(bell));
    if (!blob) throw new Error('card render produced no image');
    return blob;
  } finally {
    host.remove();
  }
}

/**
 * The first card of a page load takes about seven seconds; every one after it
 * takes a tenth of that, because the browser has by then inlined the fonts and
 * walked this shape of markup through SVG and canvas once. So do that once,
 * unseen, while the person is still writing — then the card they actually made
 * lands the moment they are done. Idempotent; failures are nobody's business.
 */
let warmed: Promise<void> | undefined;
export function warmCardRaster(html: string): Promise<void> {
  warmed ??= new Promise<void>((done) => {
    const run = () => { void cardToPng(html).then(() => done(), () => done()); };
    if (typeof requestIdleCallback === 'function') requestIdleCallback(run, { timeout: 2000 });
    else setTimeout(run, 200);
  });
  return warmed;
}
